/**
 * Who may be handed a service ticket -- the ONE definition, shared by the server and (next) the client pickers.
 *
 * MOVED here from functions/src/field-service/services/assignablePeople.ts (people-picker lane, PR 1) with NO
 * behaviour change: the pure rule lives in the shared package so the browser and Cloud Functions run the same code
 * (precedent: isTestAccountUser, testAccount.ts). The Firestore read (`loadAssignablePeople`) stays server-side.
 * Pinned by functions/src/field-service/__tests__/assignablePeople.parity.test.ts.
 *
 * A ticket's `Assigned To` is an AppSheet ref keyed on `appsheet_onpoint_users.Email`, so the
 * candidate list starts there (org-scoped, never platform-wide -- #4650). Two independent
 * signals then remove people, and both are needed because each one alone has let a departed
 * employee back into an assignee picker:
 *
 *   1. The OnPoint Users row itself: soft-deleted by the sync (`_deleted === true`), or
 *      `Status === "Inactive"`.
 *   2. The linked OfficeHub login, when there is one (matched by email within the org):
 *      `isCurrentEmployee === false` is HR's positive "this person has left" signal, mirrored
 *      onto `users/{uid}` by `hr_mirrorRoleTags`; `isActive === false` is a disabled login.
 *      `users.isActive` alone is NOT enough -- nothing flips it on termination, and production
 *      once carried an active login for someone who had resigned (see
 *      `isNotCurrentEmployeeUser` in support/assigneeResolution.ts, the same rule).
 *
 * A MISSING flag is not "departed". Shared mailboxes and technicians with no OfficeHub login
 * have no `users` doc (or no employment flag) and stay assignable -- only an explicit `false`
 * excludes someone.
 *
 * `buildAssignablePeople` is pure (rows in, people out) so every exclusion is pinned by a plain test. The
 * Firestore read that feeds it (`loadAssignablePeople`) lives server-side in
 * functions/src/field-service/services/assignablePeople.ts. (Paths named below are server paths.)
 */

/** One person a ticket (or an owner-grid cell) may be handed to. */
export interface AssignablePerson {
  /** The canonical `appsheet_onpoint_users.Email` spelling -- what AppSheet's ref matches. */
  email: string;
  /** `email`, lower-cased and trimmed. The join key for every comparison. */
  emailLower: string;
  displayName: string;
  /**
   * The `appsheet_onpoint_users` DOCUMENT id -- the stable locator (defaultAssignee.ts's header
   * explains why a person is resolved through this row rather than an HR email field).
   */
  onPointUserDocId: string;
  /** The linked OfficeHub login, when one exists in the same org. Null for shared mailboxes. */
  uid: string | null;
}

/** A raw document as read: its id plus its data. */
export interface AssignablePeopleRawDoc {
  id: string;
  data: Record<string, unknown>;
}

/**
 * @param {unknown} value  Any Firestore value.
 * @return {string} The trimmed string, or "" for anything that is not a string.
 */
function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * The name people see for an OnPoint Users row -- same precedence as the client's
 * `resolveTechnicianDisplayName` (hooks/fieldService/useTechnicianNames.ts), so the grid and
 * every picker show the same name for the same person.
 *
 * @param {Record<string, unknown>} row  An `appsheet_onpoint_users` document.
 * @return {string} The display name, or "" when the row carries none.
 */
export function onPointDisplayName(row: Record<string, unknown>): string {
  const full = text(row["Full Name"]);
  if (full) return full;
  const computed = text(row["_ComputedName"]);
  if (computed) return computed;
  const first = text(row["Preferred First Name"]) || text(row["First Name"]);
  return `${first} ${text(row["Last Name"])}`.trim();
}

/**
 * True when the OnPoint Users row itself rules the person out.
 *
 * @param {Record<string, unknown>} row  An `appsheet_onpoint_users` document.
 * @return {boolean} True for a soft-deleted or `Inactive` row, or one with no usable email.
 */
export function isExcludedOnPointRow(row: Record<string, unknown>): boolean {
  if (row._deleted === true) return true;
  if (text(row.Status).toLowerCase() === "inactive") return true;
  return !text(row.Email);
}

/**
 * Whether a roster person counts as CURRENT for a list that wants "currently working" people (fleet drivers).
 * APPSHEET-CUT-3 step S2. BOTH must hold: the copy says current (Status in `currentStatuses`, trimmed, exact case, as
 * the lists did before) AND the linked OfficeHub login, when there is one, has not left (`isExcludedLinkedUser`).
 * The login can only take a person OUT, never put one in: a stale or frozen AppSheet label can therefore over-exclude
 * but can never keep a departed person on the list, and a login with a missing or garbled flag can never re-admit
 * someone AppSheet marks Inactive. A soft-deleted row or one with no email is never current.
 *
 * @param {Record<string, unknown>} row  An `appsheet_onpoint_users` document.
 * @param {Record<string, unknown> | null | undefined} linked  The same-org `users/{uid}` document, or null/undefined.
 * @param {ReadonlySet<string>} currentStatuses  The Status values that mean current.
 * @return {boolean} True when the person is current.
 */
export function isCurrentRosterPerson(
  row: Record<string, unknown>,
  linked: Record<string, unknown> | null | undefined,
  currentStatuses: ReadonlySet<string>
): boolean {
  if (row._deleted === true || !text(row.Email)) return false;
  if (!currentStatuses.has(text(row.Status))) return false;
  return !(linked && isExcludedLinkedUser(linked));
}

/**
 * True when the linked OfficeHub login positively says this person is not assignable.
 * Only an explicit `false` counts -- see the file header.
 *
 * @param {Record<string, unknown>} user  A `users/{uid}` document.
 * @return {boolean} True for a departed employee or a disabled login.
 */
export function isExcludedLinkedUser(user: Record<string, unknown>): boolean {
  return user.isCurrentEmployee === false || user.isActive === false;
}

/**
 * Build the assignable set from rows the caller has ALREADY restricted to one organization.
 *
 * Duplicate OnPoint rows for the same email (case-insensitively) collapse to the first one --
 * the same "first writer wins" rule `loadUserEmailIndex` in projects/serviceLogApi.ts uses, so
 * the canonical spelling here matches the one the ticket writer resolves.
 *
 * @param {AssignablePeopleRawDoc[]} onPointRows  `appsheet_onpoint_users` docs for the org.
 * @param {AssignablePeopleRawDoc[]} users  `users` docs for the same org.
 * @return {AssignablePerson[]} Assignable people, sorted by display name.
 */
export function buildAssignablePeople(
  onPointRows: readonly AssignablePeopleRawDoc[],
  users: readonly AssignablePeopleRawDoc[]
): AssignablePerson[] {
  const usersByEmail = new Map<string, AssignablePeopleRawDoc>();
  for (const user of users) {
    const email = text(user.data.email).toLowerCase();
    if (email && !usersByEmail.has(email)) usersByEmail.set(email, user);
  }

  const byEmail = new Map<string, AssignablePerson>();
  for (const row of onPointRows) {
    if (isExcludedOnPointRow(row.data)) continue;
    const email = text(row.data.Email);
    const emailLower = email.toLowerCase();
    if (byEmail.has(emailLower)) continue;
    const linked = usersByEmail.get(emailLower);
    if (linked && isExcludedLinkedUser(linked.data)) continue;
    byEmail.set(emailLower, {
      email,
      emailLower,
      displayName: onPointDisplayName(row.data) || email,
      onPointUserDocId: row.id,
      uid: linked ? linked.id : null,
    });
  }

  return Array.from(byEmail.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
}
